import {
  FC,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { Form as FormAntd } from 'antd';

import QuotationService from '../../../../../../shared/services/quotation';

import { SavedQuotesContext } from './SavedQuotesContext';

import { SavedQuotesTableInterface } from '../../interface';
import {
  BOOLEAN_VALUES,
  ComboWithOptions,
  Group,
  SelectOption,
} from '../../../../../../shared/interfaces';
import { QuotationGroups } from '../../../../NewQuote/containers';

export const SavedQuotesProvider: FC<PropsWithChildren> = ({ children }) => {
  const [savedQuotesForm] = FormAntd.useForm<SavedQuotesTableInterface>();

  const [allDataTable, setAllDataTable] = useState<SavedQuotesTableInterface[]>(
    []
  );
  const [dataTable, setDataTable] = useState<SavedQuotesTableInterface[]>([]);
  const [isLoadingTable, setIsLoadingTable] = useState<boolean>(false);
  const [groupsList, setGroupsList] = useState<ComboWithOptions[]>([]);
  const [groupsOptionsList, setGroupsOptionsList] = useState<SelectOption[]>(
    []
  );

  const [articleGroups, setArticleGroups] = useState<Array<Group>>([]);

  const getSavedQuotes = useCallback(async () => {
    setIsLoadingTable(true);

    try {
      const response = await QuotationService.getSavedQuotes();

      const data: Array<SavedQuotesTableInterface> = response.data.cotizaciones.map(({
        codigoArticulo,
        codigoRama,
        descripcionArticulo,
        descripcionOperacion,
        descripcionRama,
        estado,
        fecha,
        nombreAsegurado,
        numeroDeCotizacion,
        permiteRetomar,
        tipoOperacion,
      }) => {
        return {
          allowResume: permiteRetomar === BOOLEAN_VALUES.TRUE,
          article: String(codigoArticulo),
          articleDescription: descripcionArticulo,
          branch: String(codigoRama),
          branchDescription: descripcionRama,
          entryDate: fecha,
          key: `${codigoArticulo}-${numeroDeCotizacion}`,
          nameAndSurname: nombreAsegurado,
          operation: String(tipoOperacion),
          operationDescription: descripcionOperacion,
          quotationNumber: String(numeroDeCotizacion),
          state: estado,
        };
      });

      setAllDataTable(data);
      setDataTable(data);
    } catch (error) {
    } finally {
      setIsLoadingTable(false);
    }
  }, [
    setIsLoadingTable,
    setAllDataTable,
    setDataTable,
  ]);

  const getArticleGroups = useCallback(async () => {
    try {
      const response = await QuotationService.getArticleGroups();

      const data = response.data?.Grupos?.map(({ descripcion, articulos }) => ({
        label: descripcion ?? 'Sin label',
        title: descripcion ?? 'Sin título',
        options: articulos.map(({ codigo, descLarga }) => ({
          label: descLarga ?? 'Sin descripción',
          value: codigo ?? 'Sin código',
        })),
      }));

      const dataOptions = response.data.Grupos.map(({ articulos }) =>
        articulos.map(({ descLarga, codigo }) => ({
          label: descLarga ?? 'Sin descripción',
          value: codigo ?? 'Sin código',
        }))
      ).flat();

      setGroupsOptionsList(dataOptions);
      setGroupsList(data);
      setArticleGroups(response.data.Grupos);
    } catch (error) {}
  }, [
    setGroupsOptionsList,
    setGroupsList,
    setArticleGroups,
  ]);

  const getQuotationGroupByArticle = useCallback((articleId: string): QuotationGroups => {
    
    const group = articleGroups.find( el => el.articulos.some( article => Number(article.codigo) === Number(articleId)));
    
    return (group?.codigo as QuotationGroups) ?? QuotationGroups.AP;
  }, [articleGroups])

  useEffect(() => {
    Promise.all([getArticleGroups(), getSavedQuotes()]);
  }, []);

  const values = useMemo(
    () => ({
      allDataTable,
      dataTable,
      groupsList,
      groupsOptionsList,
      isLoadingTable,
      savedQuotesForm,
      setDataTable,
      setIsLoadingTable,
      articleGroups,
      getQuotationGroupByArticle,
    }),
    [
      allDataTable,
      dataTable,
      groupsList,
      groupsOptionsList,
      isLoadingTable,
      savedQuotesForm,
      setDataTable,
      setIsLoadingTable,
      articleGroups,
      getQuotationGroupByArticle
    ]
  );

  return (
    <SavedQuotesContext.Provider value={values}>
      {children}
    </SavedQuotesContext.Provider>
  );
};
